import React, { useMemo } from 'react';

import { actions } from '@lumapps/communities/ducks/frontOfficeSlice';
import { isConfigurationWizardOpenSelector } from '@lumapps/communities/ducks/selectors';
import { useCommunity } from '@lumapps/communities/hooks/useCommunity';
import { webviewSpaceSettingsViewRoute } from '@lumapps/community-webview/routes';
import { customerIdSelector } from '@lumapps/customer/ducks/selectors';
import { instanceIdSelector } from '@lumapps/instance/ducks/selectors';
import { sendWebviewMobileMessage } from '@lumapps/mobile-webview/message-emit';
import { isInstanceSuperAdmin as isInstanceSuperAdminSelector } from '@lumapps/permissions';
import { useDispatch, useSelector } from '@lumapps/redux/react';
import { useHash, useRouteMatch } from '@lumapps/router';
import { useSpace } from '@lumapps/spaces/hooks/useSpace';
import type { SpaceRouteParameters } from '@lumapps/spaces/types';
import { currentLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { accountTypeSelector, getConnectedUserLoginProvider } from '@lumapps/user/ducks/selectors';

import { EDIT_COMMUNITY_DIALOG_EVENT } from '../constants';
import { getDisplayedSteps } from '../utils/getDisplayedSteps';

/**
 * Retrieve the base configuration for the Configuration dialog
 * Used both for Spaces and communities
 * */
export const useConfigurationDialog = () => {
    const dispatch = useDispatch();
    const { id, canEdit: canEditSpace, isSpace } = useSpace();
    const { canEditCommunity, currentCommunityId } = useCommunity();
    // Listen to hash change in case dialog is opened from notification within a community.
    const [hash, setHash] = useHash();
    const webviewMatch = useRouteMatch<SpaceRouteParameters>(webviewSpaceSettingsViewRoute);

    const isInstanceSuperAdmin = useSelector(isInstanceSuperAdminSelector);
    const customerId = useSelector(customerIdSelector);
    const instanceId = useSelector(instanceIdSelector);
    const userLanguage = useSelector(currentLanguageSelector);
    const userAccountType = useSelector(accountTypeSelector);
    const userLoginProvider = useSelector(getConnectedUserLoginProvider);
    const isConfigurationWizardOpen = useSelector(isConfigurationWizardOpenSelector);

    const spaceId = useMemo(() => {
        if (isSpace && id === undefined) {
            return webviewMatch?.params?.id;
        }
        return id;
    }, [isSpace, id, webviewMatch?.params?.id]);

    // Open dialog if redux state is at true or if hash is in url or if webview
    const isOpen = isConfigurationWizardOpen || hash === `#${EDIT_COMMUNITY_DIALOG_EVENT}` || webviewMatch?.isExact;

    const handleClose = React.useCallback(() => {
        if (webviewMatch?.isExact) {
            sendWebviewMobileMessage({
                type: 'close',
            });
            return;
        }

        dispatch(actions.closeConfigurationWizard());

        if (hash === `#${EDIT_COMMUNITY_DIALOG_EVENT}`) {
            // Remove hash on close.
            setHash('');
        }
    }, [dispatch, hash, setHash, webviewMatch?.isExact]);

    const displayedSteps = useMemo(
        () => getDisplayedSteps(isInstanceSuperAdmin, isSpace),
        [isSpace, isInstanceSuperAdmin],
    );

    const communityId = spaceId || currentCommunityId;
    const isConfigurationEnabled = (spaceId && canEditSpace) || (currentCommunityId && canEditCommunity);

    return {
        customerId,
        instanceId,
        userLanguage,
        userAccountType,
        userLoginProvider,
        isOpen,
        handleClose,
        displayedSteps,
        isConfigurationEnabled,
        communityId,
    };
};
